import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, Citrus } from 'lucide-react';
import { useLanguage } from '@/i18n/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const { t } = useLanguage();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('checking');

  useEffect(() => {
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setStatus('paid');
          return;
        }
      } catch (e) {
        // keep polling on transient errors
      }
      attempts += 1;
      if (attempts < 12) {
        timer = setTimeout(poll, 2000);
      } else {
        setStatus('pending');
      }
    };
    if (sessionId) {
      poll();
    } else {
      setStatus('pending');
    }
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-brand-cream text-brand-green flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white border-2 border-brand-green/15 rounded-3xl p-10 shadow-xl text-center space-y-6" data-testid="payment-success-page">
        <div className="w-14 h-14 rounded-full bg-brand-orange mx-auto flex items-center justify-center text-white -rotate-6">
          <Citrus className="w-7 h-7" />
        </div>
        <h1 className="font-display text-4xl uppercase text-brand-green">{t.success.title}</h1>

        {status === 'checking' && (
          <p className="text-sm text-brand-green/70 inline-flex items-center gap-2" data-testid="payment-status-checking">
            <Loader2 className="w-4 h-4 animate-spin text-brand-orange" /> {t.success.checking}
          </p>
        )}
        {status === 'paid' && (
          <div className="space-y-3" data-testid="payment-status-paid">
            <CheckCircle2 className="w-10 h-10 text-brand-leaf mx-auto" />
            <p className="text-sm text-brand-green/80 leading-relaxed">{t.success.confirmed}</p>
          </div>
        )}
        {status === 'pending' && (
          <p className="text-sm text-brand-green/70 leading-relaxed" data-testid="payment-status-pending">{t.success.pending}</p>
        )}

        <Link
          to="/"
          data-testid="success-back-home-btn"
          className="inline-flex items-center justify-center px-8 py-3 rounded-2xl bg-brand-orange text-white text-sm font-extrabold uppercase tracking-wide hover:bg-brand-citrus transition-colors shadow-lg citrus-glow"
        >
          {t.success.back}
        </Link>
      </div>
    </div>
  );
}
